/**
 * StemRoutingPreview — modal shown BEFORE stem extraction begins.
 *
 * Shows the user exactly which destination channel each stem will
 * land on (e.g. "Vocals → CH 04, Drums → CH 05, Bass → CH 06…")
 * so they can confirm the routing before the (slow + paid) fal.ai
 * upload kicks off. Prevents the post-extraction "where did my stems
 * go?" surprise.
 *
 * iter-167 — initial routing preview with auto-suggested targets.
 * iter-168 — per-stem destination override + conflict detection +
 * persistence so power users can dial in their preferred channel
 * layout (e.g. "always send vocals to CH 16 next to my master fader").
 */
import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Scissors, X, ArrowRight, Layers3, AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

// htdemucs_6s returns exactly these 6 stems; ordering matches what
// fal.ai sends back so the preview matches what really happens.
const PREDICTED_STEMS = [
  { key: "vocals", label: "Vocals",  gradient: "from-rose-400 to-fuchsia-400" },
  { key: "drums",  label: "Drums",   gradient: "from-amber-400 to-orange-400" },
  { key: "bass",   label: "Bass",    gradient: "from-emerald-400 to-cyan-400" },
  { key: "other",  label: "Other",   gradient: "from-slate-300 to-slate-500" },
  { key: "guitar", label: "Guitar",  gradient: "from-violet-400 to-fuchsia-400" },
  { key: "piano",  label: "Piano",   gradient: "from-sky-400 to-indigo-400" },
];

const TOTAL_CHANNELS = 64;
const ROUTING_STORAGE_KEY = "elpm:stem-routing-overrides";

function padCh(n) { return String(n + 1).padStart(2, "0"); }

function readSavedOverrides() {
  try {
    const raw = JSON.parse(localStorage.getItem(ROUTING_STORAGE_KEY) || "{}") || {};
    return PREDICTED_STEMS.map((s) => {
      const v = raw[s.key];
      return Number.isInteger(v) && v >= 0 && v < TOTAL_CHANNELS ? v : null;
    });
  } catch (e) { void e; return [null, null, null, null, null, null]; }
}

function computeInitialRouting(targets) {
  const saved = readSavedOverrides();
  return PREDICTED_STEMS.map((_, i) => saved[i] ?? targets?.[i] ?? null);
}

// Outer wrapper switches `key` so the inner component remounts and
// `useState`'s lazy initializer recomputes every time the modal opens.
// This dodges the React 19-strict "set-state-in-effect" rule entirely.
export default function StemRoutingPreview(props) {
  if (!props.open) {
    return <AnimatePresence>{null}</AnimatePresence>;
  }
  return (
    <StemRoutingInner
      key={`${props.sourceIdx}-${(props.targets || []).join("-")}`}
      {...props}
    />
  );
}

function StemRoutingInner({
  sourceIdx,
  targets,          // auto-suggested free channel indices, length up to 6
  occupiedChannels, // Set<number> of channel indices that currently have audio
  onConfirm,
  onCancel,
}) {
  const [routing, setRouting] = useState(() => computeInitialRouting(targets));

  const setStemTarget = (stemIdx, ch) => {
    setRouting((prev) => prev.map((v, i) => (i === stemIdx ? ch : v)));
  };

  const resetToAuto = () => {
    setRouting(PREDICTED_STEMS.map((_, i) => targets?.[i] ?? null));
    try { localStorage.removeItem(ROUTING_STORAGE_KEY); } catch (e) { void e; }
  };

  // Conflict detection: same channel picked for more than one stem.
  const conflicts = useMemo(() => {
    const seen = {};
    const conflicting = new Set();
    routing.forEach((v) => {
      if (v == null) return;
      if (seen[v]) conflicting.add(v);
      seen[v] = (seen[v] || 0) + 1;
    });
    return conflicting;
  }, [routing]);

  const occSet = useMemo(() => occupiedChannels instanceof Set ? occupiedChannels : new Set(occupiedChannels || []), [occupiedChannels]);
  const overwriteCount = useMemo(() => routing.filter((v) => v != null && occSet.has(v)).length, [routing, occSet]);
  const assignedCount = routing.filter((v) => v != null).length;
  const hasConflicts = conflicts.size > 0;
  const noTargets = assignedCount === 0;

  const handleConfirm = () => {
    // Persist user's per-stem preferences so next extraction defaults to
    // the same routing without them having to re-pick.
    try {
      const toSave = {};
      PREDICTED_STEMS.forEach((s, i) => {
        if (routing[i] != null) toSave[s.key] = routing[i];
      });
      localStorage.setItem(ROUTING_STORAGE_KEY, JSON.stringify(toSave));
    } catch (e) { void e; }
    onConfirm(routing);
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="stem-routing-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] grid place-items-center bg-black/70 p-4 backdrop-blur-sm"
      >
          <motion.div
            initial={{ scale: 0.95, y: 12 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 12 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="w-full max-w-md rounded-3xl border border-white/10 bg-zinc-950/95 p-5 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]"
          >
            <div className="mb-4 flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white">
                <Layers3 className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-lg font-semibold text-white">Confirm stem routing</h2>
                <p className="font-mono text-[10px] uppercase tracking-wider text-white/45">
                  Source · CH {padCh(sourceIdx)} · Demucs 6-stem
                </p>
              </div>
              <button
                type="button"
                data-testid="stem-routing-reset"
                onClick={resetToAuto}
                title="Reset all stems to the auto-suggested free channels and clear saved overrides"
                className="rounded-full bg-white/[0.05] p-1.5 text-white/55 transition hover:bg-white/[0.12] hover:text-white"
                aria-label="Reset to auto"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                type="button"
                data-testid="stem-routing-close"
                onClick={onCancel}
                className="rounded-full bg-white/[0.05] p-1.5 text-white/55 transition hover:bg-white/[0.12] hover:text-white"
                aria-label="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-1.5">
              {PREDICTED_STEMS.map((s, i) => {
                const target = routing[i];
                const isConflict = target != null && conflicts.has(target);
                const isOverwrite = target != null && occSet.has(target);
                return (
                  <div
                    key={s.key}
                    data-testid={`stem-routing-row-${s.key}`}
                    className={`flex items-center justify-between rounded-xl border px-3 py-2 transition ${
                      isConflict
                        ? "border-rose-400/50 bg-rose-500/[0.08]"
                        : isOverwrite
                          ? "border-amber-400/30 bg-amber-500/[0.05]"
                          : "border-white/[0.06] bg-white/[0.02]"
                    }`}
                  >
                    <span className={`bg-gradient-to-r ${s.gradient} bg-clip-text font-display text-sm font-semibold text-transparent`}>
                      {s.label}
                    </span>
                    <span className="flex items-center gap-2">
                      <ArrowRight className="h-3 w-3 text-white/35" />
                      <select
                        data-testid={`stem-routing-select-${s.key}`}
                        value={target == null ? "" : String(target)}
                        onChange={(e) => {
                          const v = e.target.value;
                          setStemTarget(i, v === "" ? null : Number(v));
                        }}
                        className={`min-w-[112px] cursor-pointer rounded-md border bg-black/40 px-2 py-1 font-mono text-[11px] tabular-nums outline-none focus:border-cyan-300/60 ${
                          isConflict
                            ? "border-rose-400/50 text-rose-100"
                            : isOverwrite
                              ? "border-amber-400/35 text-amber-100"
                              : "border-white/15 text-white/85"
                        }`}
                      >
                        <option value="">— skip this stem —</option>
                        {Array.from({ length: TOTAL_CHANNELS }, (_, ch) => (
                          <option key={ch} value={String(ch)}>
                            {`CH ${padCh(ch)}${occSet.has(ch) ? " · in use" : ""}`}
                          </option>
                        ))}
                      </select>
                    </span>
                  </div>
                );
              })}
            </div>

            {hasConflicts && (
              <p data-testid="stem-routing-conflict-warning" className="mt-3 flex items-start gap-2 rounded-lg border border-rose-400/35 bg-rose-500/[0.10] px-3 py-2 font-mono text-[10px] leading-relaxed text-rose-100">
                <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                <span>Two or more stems target the same channel ({[...conflicts].map((c) => `CH ${padCh(c)}`).join(", ")}). Pick distinct destinations before extracting.</span>
              </p>
            )}
            {!hasConflicts && overwriteCount > 0 && (
              <p data-testid="stem-routing-overwrite-warning" className="mt-3 flex items-start gap-2 rounded-lg border border-amber-400/30 bg-amber-500/[0.08] px-3 py-2 font-mono text-[10px] leading-relaxed text-amber-100">
                <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                <span>{overwriteCount} stem{overwriteCount === 1 ? "" : "s"} will overwrite existing audio on the chosen channel{overwriteCount === 1 ? "" : "s"}. Pick a different destination if you want to keep the current track.</span>
              </p>
            )}
            {!hasConflicts && noTargets && (
              <p data-testid="stem-routing-no-targets-warning" className="mt-3 flex items-start gap-2 rounded-lg border border-rose-400/30 bg-rose-500/[0.08] px-3 py-2 font-mono text-[10px] leading-relaxed text-rose-100">
                <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                <span>All 6 stems are set to &ldquo;skip&rdquo; — nothing will be extracted. Pick a destination channel for at least one stem.</span>
              </p>
            )}

            <div className="mt-5 flex items-center gap-2">
              <Button
                data-testid="stem-routing-cancel"
                variant="ghost"
                onClick={onCancel}
                className="flex-1 rounded-xl bg-white/[0.04] text-white/75 hover:bg-white/[0.10]"
              >
                Cancel
              </Button>
              <Button
                data-testid="stem-routing-confirm"
                onClick={handleConfirm}
                disabled={hasConflicts || noTargets}
                className="flex-1 rounded-xl bg-gradient-to-r from-cyan-500 to-sky-500 text-white shadow-[0_0_18px_rgba(34,211,238,0.4)] transition hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Scissors className="mr-2 h-4 w-4" />
                Extract {assignedCount}/6
              </Button>
            </div>

            <p className="mt-3 text-center font-mono text-[9px] uppercase tracking-wider text-white/35">
              fal.ai Demucs 6-stem · ~30–90 s · uses your fal.ai credits · choices remembered for next time
            </p>
          </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
